import type { ToneNotation } from "@kensio/pinyinjs";

import type { CanonicalEntry } from "../segmenting/canonical-entry.js";
import type { SegmenterElements } from "./segmenter-elements.js";
import {
  SegmenterMeanings,
  type SegmenterMeaningsProperties,
} from "./segmenter-meanings.js";
import { showOrder } from "./segmenter-ordering.js";
import { wordRows } from "./segmenter-word-list.js";
import { summaryText } from "./segmenter-summary.js";
import {
  type SegmentedWord,
  totalWords,
} from "../segmenting/segmented-words.js";
import { orderedWords, type WordOrder } from "../segmenting/word-order.js";

/**
 * Putting the word list on the page, and saying what order it is in.
 *
 * Separate from `segmenter-session.ts`, which decides when to read the text
 * and what to read it with, from `segmenter-input.ts`, which holds the box to
 * its cap, and from `segmenter-meanings.ts`, which fills in a meaning where it
 * stands rather than by drawing the list again. This is the output half.
 */

/** What the view needs beside the page it writes to. */
export type SegmenterViewProperties = Omit<SegmenterMeaningsProperties, "list">;

/** Everything the segmenter writes to the page. */
export class SegmenterView {
  readonly #elements: SegmenterElements;
  readonly #properties: SegmenterViewProperties;
  readonly #meanings: SegmenterMeanings;

  constructor(
    elements: SegmenterElements,
    properties: SegmenterViewProperties,
  ) {
    this.#elements = elements;
    this.#properties = properties;
    this.#meanings = new SegmenterMeanings({
      list: elements.list,
      ...properties,
    });
  }

  /**
   * Put a list of words on the page, in the order asked for.
   *
   * The ordering happens here rather than in the tool because it is the only
   * thing reordering changes: the same words come back in a different sequence
   * and nothing has to be read again.
   */
  show(
    words: readonly SegmentedWord[],
    order: WordOrder,
    notation: ToneNotation,
    held?: ReadonlySet<string>,
  ): void {
    const { list, summary, empty, input, icons } = this.#elements;
    const shown = orderedWords(words, order);

    list.replaceChildren(
      ...wordRows({
        words: shown,
        notation,
        held,
        entryOf: this.#properties.entryOf,
        open: this.#meanings.opened(),
        icons,
      }),
    );
    summary.textContent = summaryText(shown, totalWords(shown));

    empty.hidden = !(input.value.trim() !== "" && shown.length === 0);

    showOrder(this.#elements.ordering, order);
  }

  /** Put one word's meaning in its row, now that the site has said it. */
  showGloss(headword: string, entry: CanonicalEntry): void {
    this.#meanings.show(headword, entry);
  }
}
